/**
 * Field action links — the single source of truth for the four things a
 * soldier does with a phone in their hand: call the contact, open WhatsApp,
 * launch navigation to the destination, and dial a crewmate.
 *
 * The rules live here rather than inside the field view, so a `tel:` href is
 * built once and every screen that offers one builds it the same way. Nothing
 * here touches the DOM — the components only render what these return.
 */
import { normalizePhone } from './phone';
import type { Task } from '../types/task';

/** `0501112233` → `972501112233` — the form WhatsApp expects. */
export function internationalPhone(raw: string | null | undefined): string {
  const local = normalizePhone(String(raw ?? ''));
  if (!local) return '';
  return local.startsWith('0') ? `972${local.slice(1)}` : local;
}

/** `tel:` href for the big call button, or `null` when there is no number. */
export function telHref(raw: string | null | undefined): string | null {
  const phone = normalizePhone(String(raw ?? ''));
  return phone ? `tel:${phone}` : null;
}

/** `https://wa.me/…` href, with an optional pre-filled message. */
export function whatsappHref(raw: string | null | undefined, message?: string): string | null {
  const phone = internationalPhone(raw);
  if (!phone) return null;
  const text = String(message ?? '').trim();
  return text ? `https://wa.me/${phone}?text=${encodeURIComponent(text)}` : `https://wa.me/${phone}`;
}

/**
 * A WhatsApp share link with no recipient — `https://wa.me/?text=…`.
 *
 * WhatsApp asks who to send it to, which is exactly what a dispatch order needs:
 * it goes to a group, not to one number. This is also the fallback a browser
 * without `navigator.share()` for files gets, so the order still travels as
 * formatted text when it cannot travel as an image.
 */
export function whatsappTextHref(message: string | null | undefined): string | null {
  const text = String(message ?? '').trim();
  return text ? `https://wa.me/?text=${encodeURIComponent(text)}` : null;
}

// ── Navigation ───────────────────────────────────────────────────────

/** The navigation apps the launcher offers. */
export const NAVIGATION_APPS = ['waze', 'google'] as const;

export type NavigationApp = (typeof NAVIGATION_APPS)[number];

/** Every launcher reads as the same action — only the app changes. */
export const NAVIGATION_ACTION_LABEL = 'פתיחת ניווט';

export const NAVIGATION_APP_LABELS: Record<NavigationApp, string> = {
  waze: `${NAVIGATION_ACTION_LABEL} ב-Waze`,
  google: `${NAVIGATION_ACTION_LABEL} ב-Google Maps`,
};

export function wazeHref(address: string | null | undefined): string | null {
  const query = String(address ?? '').trim();
  return query ? `https://waze.com/ul?q=${encodeURIComponent(query)}&navigate=yes` : null;
}

export function googleMapsHref(address: string | null | undefined): string | null {
  const query = String(address ?? '').trim();
  return query
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(query)}`
    : null;
}

export interface NavigationLink {
  id: NavigationApp | 'saved';
  label: string;
  href: string;
}

/**
 * Every way this task can be navigated to. The manager's own pasted link comes
 * first when there is one — it is the address they actually meant — and the two
 * app launchers are built from the written destination behind it.
 */
export function navigationLinks(task: Pick<Task, 'destinationAddress' | 'destinationLink'>): NavigationLink[] {
  const links: NavigationLink[] = [];
  const saved = String(task.destinationLink ?? '').trim();
  if (saved) links.push({ id: 'saved', label: NAVIGATION_ACTION_LABEL, href: saved });

  const address = String(task.destinationAddress ?? '').trim();
  const waze = wazeHref(address);
  if (waze) links.push({ id: 'waze', label: NAVIGATION_APP_LABELS.waze, href: waze });
  const google = googleMapsHref(address);
  if (google) links.push({ id: 'google', label: NAVIGATION_APP_LABELS.google, href: google });

  return links;
}

/** The one link a single "פתיחת ניווט" button should open, when there is one. */
export function primaryNavigationHref(
  task: Pick<Task, 'destinationAddress' | 'destinationLink'>,
): string | null {
  return navigationLinks(task)[0]?.href ?? null;
}
